import React, { useState } from 'react'
import styled from 'styled-components/macro'
import {
  canOllieOrderingConfirm,
  OLLIE_ORDERING_CONFIRM_LABEL,
  OLLIE_ORDERING_CONFIRMING_LABEL,
  OLLIE_ORDERING_PROPOSAL_HEADING,
  canOllieOrderingEditQuantity,
  formatOllieOrderingQuantity,
  hasOllieOrderingOpenAsks,
  isOllieOrderingAddCatalogueLine,
  isOllieOrderingQuantityAsk,
  isOllieOrderingSkippedLine,
  isOllieOrderingWhichOneLine,
  ollieOrderingConfirmSummary,
  OLLIE_ORDERING_WONT_ADD_BADGE,
  ollieOrderingBlockedHint,
  ollieOrderingCatalogueCandidates,
  ollieOrderingChoiceBadge,
  ollieOrderingLineHint,
  ollieOrderingLineTitle,
  ollieOrderingStepperQuantity,
  ollieOrderingWhichOneCandidates,
  splitOllieOrderingChoices,
  OLLIE_ORDERING_EMPTY_HINT,
} from 'src/utils/ollieOrderingProposal'

const OllieOrderingProposal = ({
  proposal,
  onPickListCandidate,
  onEditQuantity,
  onRemoveLine,
  onConfirm,
  picking = false,
  confirming = false,
  quantityEdits = null,
}) => {
  const [expandedKeys, setExpandedKeys] = useState(() => new Set())
  if (!proposal) {
    return null
  }
  const lines = Array.isArray(proposal.lines) ? proposal.lines : []
  if (!lines.length) {
    return (
      <List data-testid='ollie-ordering-proposal' aria-label='Proposed order'>
        {proposal.text ? <Said>You said: {proposal.text}</Said> : null}
        <EmptyHint data-testid='ollie-ordering-empty'>
          {OLLIE_ORDERING_EMPTY_HINT}
        </EmptyHint>
      </List>
    )
  }
  const busy = picking || confirming
  const openAsks = hasOllieOrderingOpenAsks(proposal)
  const blockedHint = ollieOrderingBlockedHint(proposal)
  const showConfirm =
    Boolean(onConfirm) && !openAsks && canOllieOrderingConfirm(proposal)
  const confirmSummary = ollieOrderingConfirmSummary(proposal)

  // Steppers answer the click on screen and save once the chef settles, so
  // only the line being saved goes quiet — the rest stay clickable.
  const bumpQuantity = (line, key, quantity) => {
    if (!Number.isFinite(quantity) || quantity < 1) return
    if (quantityEdits) {
      return quantityEdits.setQuantity({ key, phrase: line.phrase, quantity })
    }
    return onEditQuantity({ phrase: line.phrase, quantity })
  }

  return (
    <List data-testid='ollie-ordering-proposal' aria-label='Proposed order'>
      <Heading>{OLLIE_ORDERING_PROPOSAL_HEADING}</Heading>
      {proposal.text ? <Said>You said: {proposal.text}</Said> : null}
      <Lines>
        {lines.map((line, index) => {
          const whichOne = isOllieOrderingWhichOneLine(line)
          const addCatalogue = isOllieOrderingAddCatalogueLine(line)
          const quantityAsk = isOllieOrderingQuantityAsk(line)
          const title = ollieOrderingLineTitle(line) || `Item ${index + 1}`
          const hint = ollieOrderingLineHint(line)
          const quantity = formatOllieOrderingQuantity(line)
          const candidates = whichOne
            ? ollieOrderingWhichOneCandidates(line)
            : ollieOrderingCatalogueCandidates(line)
          const { featured, rest } = splitOllieOrderingChoices(candidates)
          const expandKey = `${line.phrase || title}-${index}`
          const expanded = expandedKeys.has(expandKey)
          const visibleCandidates = expanded ? [...featured, ...rest] : featured
          const choicesTestId = whichOne
            ? 'ollie-ordering-which-one'
            : addCatalogue
            ? 'ollie-ordering-add-catalogue'
            : null
          const pickTestId = whichOne
            ? 'ollie-ordering-pick'
            : 'ollie-ordering-add'
          const canEditQty =
            canOllieOrderingEditQuantity(line) && onEditQuantity
          const stepKey = String(line.phrase || title)
          const savedQty = ollieOrderingStepperQuantity(line)
          const stepperQty = quantityEdits
            ? quantityEdits.quantityFor(stepKey, savedQty)
            : savedQty
          const stepBusy = quantityEdits
            ? confirming || quantityEdits.isSaving(stepKey)
            : busy
          const current = Number(line?.currentQuantity)
          const showCurrent =
            Number.isFinite(current) && current > 0 && canEditQty
          return (
            <Line
              key={`${line.phrase || title}-${index}`}
              data-testid='ollie-ordering-line'
              $ask={whichOne || addCatalogue || quantityAsk}
            >
              <LineMain>
                <LineTitle>{title}</LineTitle>
                {!whichOne && !addCatalogue && line.supplierTitle ? (
                  <Supplier>{line.supplierTitle}</Supplier>
                ) : null}
              </LineMain>
              {canEditQty ? (
                <Qty data-testid='ollie-ordering-qty'>
                  {showCurrent ? <Impact>{current} → </Impact> : null}
                  <StepButton
                    type='button'
                    data-testid='ollie-ordering-qty-down'
                    disabled={stepBusy || stepperQty <= 1}
                    aria-label={`Decrease ${title}`}
                    onClick={() =>
                      bumpQuantity(line, stepKey, Math.max(1, stepperQty - 1))
                    }
                  >
                    −
                  </StepButton>
                  <QtyValue data-testid='ollie-ordering-qty-value'>
                    {stepperQty}
                  </QtyValue>
                  <StepButton
                    type='button'
                    data-testid='ollie-ordering-qty-up'
                    disabled={stepBusy}
                    aria-label={`Increase ${title}`}
                    onClick={() => bumpQuantity(line, stepKey, stepperQty + 1)}
                  >
                    +
                  </StepButton>
                </Qty>
              ) : (
                <Qty data-testid='ollie-ordering-qty'>{quantity}</Qty>
              )}
              {isOllieOrderingSkippedLine(line) ? (
                <WontAdd data-testid='ollie-ordering-wont-add'>
                  {OLLIE_ORDERING_WONT_ADD_BADGE}
                </WontAdd>
              ) : null}
              {hint ? <Hint>{hint}</Hint> : null}
              {choicesTestId ? (
                <Choices data-testid={choicesTestId}>
                  {visibleCandidates.map((candidate, choiceIndex) => {
                    const badge =
                      choiceIndex === 0
                        ? ollieOrderingChoiceBadge(candidate, {
                            featured: true,
                          })
                        : ''
                    return (
                      <ChoiceButton
                        key={`${candidate.productId || candidate.title}`}
                        type='button'
                        disabled={busy || !onPickListCandidate}
                        data-testid={pickTestId}
                        $featured={choiceIndex === 0}
                        onClick={() =>
                          onPickListCandidate?.({
                            phrase: line.phrase,
                            choice: candidate.title,
                          })
                        }
                      >
                        {badge ? (
                          <ChoiceBadge data-testid='ollie-ordering-choice-badge'>
                            {badge}
                          </ChoiceBadge>
                        ) : null}
                        <ChoiceTitle>{candidate.title}</ChoiceTitle>
                        {candidate.supplierTitle ? (
                          <ChoiceMeta>{candidate.supplierTitle}</ChoiceMeta>
                        ) : null}
                      </ChoiceButton>
                    )
                  })}
                  {rest.length ? (
                    <SeeMoreButton
                      type='button'
                      data-testid='ollie-ordering-see-more'
                      disabled={busy}
                      onClick={() =>
                        setExpandedKeys(current => {
                          const next = new Set(current)
                          if (next.has(expandKey)) next.delete(expandKey)
                          else next.add(expandKey)
                          return next
                        })
                      }
                    >
                      {expanded ? 'Show less' : `See ${rest.length} more`}
                    </SeeMoreButton>
                  ) : null}
                </Choices>
              ) : null}
              {onRemoveLine ? (
                <RemoveButton
                  type='button'
                  data-testid='ollie-ordering-remove'
                  disabled={busy}
                  onClick={() => onRemoveLine({ phrase: line.phrase })}
                >
                  Remove
                </RemoveButton>
              ) : null}
            </Line>
          )
        })}
      </Lines>
      {blockedHint ? (
        <AskHint data-testid='ollie-ordering-ask-hint'>{blockedHint}</AskHint>
      ) : null}
      {showConfirm && confirmSummary ? (
        <ConfirmSummary data-testid='ollie-ordering-confirm-summary'>
          {confirmSummary}
        </ConfirmSummary>
      ) : null}
      {showConfirm ? (
        <ConfirmButton
          type='button'
          data-testid='ollie-ordering-confirm'
          disabled={busy}
          onClick={() => onConfirm()}
        >
          {confirming
            ? OLLIE_ORDERING_CONFIRMING_LABEL
            : OLLIE_ORDERING_CONFIRM_LABEL}
        </ConfirmButton>
      ) : null}
    </List>
  )
}

const List = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`

const Heading = styled.h3`
  color: ${({ theme }) => theme.colours.seaweedGreen};
  font-size: 16px;
  font-weight: 700;
  margin: 0;
`

const Said = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 13px;
  margin: 0;
`

const EmptyHint = styled.p`
  color: ${({ theme }) => theme.colours.seaweedGreen};
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  margin: 0;
`

const AskHint = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 13px;
  margin: 0;
`

const ConfirmSummary = styled.p`
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 13px;
  font-weight: 600;
  margin: 0;
`

const WontAdd = styled.span`
  align-self: flex-start;
  background: ${({ theme }) => theme.colours.panSilver};
  border-radius: 4px;
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  width: fit-content;
`

const Lines = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
`

const Line = styled.li`
  background: ${({ theme, $ask }) =>
    $ask ? 'rgba(181, 217, 34, 0.12)' : theme.colours.bgGrey};
  border-radius: 8px;
  display: grid;
  gap: 4px 12px;
  grid-template-columns: 1fr auto;
  padding: 12px 14px;
`

const LineMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const LineTitle = styled.strong`
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 15px;
  font-weight: 600;
`

const Supplier = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 13px;
`

const Qty = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colours.seaweedGreen};
  display: inline-flex;
  font-size: 16px;
  font-weight: 700;
  gap: 6px;
  white-space: nowrap;
`

const Impact = styled.span`
  font-weight: 700;
`

const QtyValue = styled.span`
  min-width: 1.25em;
  text-align: center;
`

const StepButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-radius: 6px;
  color: ${({ theme }) => theme.colours.seaweedGreen};
  cursor: pointer;
  display: inline-flex;
  font-size: 16px;
  font-weight: 700;
  height: 28px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  width: 28px;

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`

const Hint = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 13px;
  grid-column: 1 / -1;
`

const Choices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  grid-column: 1 / -1;
  margin-top: 8px;
`

const ChoiceButton = styled.button`
  background: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid
    ${({ theme, $featured }) =>
      $featured ? theme.colours.lettuceGreen : theme.colours.panSilver};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  text-align: left;

  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`

const ChoiceTitle = styled.span`
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 14px;
  font-weight: 600;
`

const ChoiceMeta = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 13px;
`

const ChoiceBadge = styled.span`
  color: ${({ theme }) => theme.colours.seaweedGreen};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`

const SeeMoreButton = styled.button`
  align-self: start;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colours.seaweedGreen};
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  padding: 0;

  &:hover,
  &:focus {
    text-decoration: underline;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colours.oliveGrey};
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  grid-column: 1 / -1;
  justify-self: start;
  margin-top: 4px;
  padding: 0;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colours.chilliRed || '#ff4d4f'};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`

const ConfirmButton = styled.button`
  align-self: flex-start;
  background: ${({ theme }) => theme.colours.lettuceGreen};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colours.seaweedGreen};
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
  margin-top: 4px;
  padding: 10px 18px;

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colours.avocadoGreen};
  }

  &:disabled {
    background: ${({ theme }) => theme.colours.panSilver};
    color: ${({ theme }) => theme.colours.oliveGrey};
    cursor: not-allowed;
  }
`

export default OllieOrderingProposal
